import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  X, 
  ArrowRight, 
  Clock, 
  TrendingUp, 
  Package, 
  BookOpen, 
  FileText,
  Command,
  Folder,
  BookMarked
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../hooks/useApi';
import { cn } from '../utils/cn';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Use the search hook
  const { results, loading: isLoading } = useSearch(query);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Restore or reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Try to restore previous search state from sessionStorage
      const savedState = sessionStorage.getItem('searchModalState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          setQuery(state.query || '');
          setActiveTab(state.activeTab || 'all');
          setShowSuggestions(!state.query || state.query.trim() === '');
          setSelectedIndex(0);
        } catch (e) {
          // If parsing fails, reset to defaults
          setQuery('');
          setSelectedIndex(0);
          setShowSuggestions(true);
          setActiveTab('all');
        }
      } else {
        // No saved state, reset to defaults
        setQuery('');
        setSelectedIndex(0);
        setShowSuggestions(true);
        setActiveTab('all');
      }
    } else {
      // Save current state when modal closes
      const stateToSave = {
        query,
        activeTab,
        timestamp: Date.now()
      };
      sessionStorage.setItem('searchModalState', JSON.stringify(stateToSave));
    }
  }, [isOpen]);

  // Update active tab when results change
  useEffect(() => {
    if (results.length > 0 && activeTab === 'all') {
      // Auto-select first available tab
      const typeOrder = ['category', 'tutorial', 'glossary', 'product'];
      const groupedResults = results.reduce((acc, result) => {
        if (!acc[result.type]) {
          acc[result.type] = [];
        }
        acc[result.type].push(result);
        return acc;
      }, {} as Record<string, typeof results>);
      
      for (const type of typeOrder) {
        if (groupedResults[type] && groupedResults[type].length > 0) {
          setActiveTab(type);
          break;
        }
      }
    }
  }, [results, activeTab]);

  // Update showSuggestions based on query
  useEffect(() => {
    if (query.trim()) {
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
    }
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const totalItems = showSuggestions ? 0 : results.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!showSuggestions && results.length > 0) {
          setSelectedIndex(prev => (prev + 1) % totalItems);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!showSuggestions && results.length > 0) {
          setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (!showSuggestions && results.length > 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [selectedIndex, results, showSuggestions, onClose]);

  // Handle result click
  const handleResultClick = useCallback((result: any) => {
    // Save search state before navigating
    const stateToSave = {
      query,
      activeTab,
      results: results.map(r => ({
        id: r.id,
        type: r.type,
        title: r.title || r.term,
        url: r.url
      })),
      timestamp: Date.now()
    };
    sessionStorage.setItem('searchModalState', JSON.stringify(stateToSave));
    sessionStorage.setItem('searchModalFromNavigation', 'true');
    
    // Close modal first
    onClose();
    
    // Small delay to ensure modal closes before navigation
    setTimeout(() => {
      // Navigate based on result type
      if (result.type === 'product') {
        navigate(`/products`);
      } else if (result.type === 'tutorial') {
        navigate(`/tutorials/${result.id}`);
      } else if (result.type === 'category') {
        // Use the URL from the result if available, otherwise construct it
        if (result.url) {
          navigate(result.url);
        } else {
          const categoryName = result.title || result.name || result.category || '';
          if (categoryName) {
            navigate(`/categories/${encodeURIComponent(categoryName)}`);
          }
        }
      } else if (result.type === 'glossary') {
        // Navigate to glossary page with the term
        const glossaryTerm = result.term || result.title || '';
        if (glossaryTerm) {
          navigate(`/glossary/${encodeURIComponent(glossaryTerm)}`);
        } else {
          navigate('/glossary');
        }
      }
    }, 100);
  }, [navigate, onClose, query, activeTab, results]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
  };

  // Get result icon
  const getResultIcon = (type: string) => {
    switch (type) {
      case 'product':
        return Package;
      case 'tutorial':
        return BookOpen;
      case 'category':
        return Folder;
      case 'glossary':
        return BookMarked;
      case 'doc':
        return FileText;
      default:
        return Search;
    }
  };

  // Get result type label
  const getResultTypeLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Product';
      case 'tutorial':
        return 'Tutorial';
      case 'category':
        return 'Category';
      case 'glossary':
        return 'Glossary';
      case 'doc':
        return 'Documentation';
      default:
        return 'Result';
    }
  };

  // Render search results
  const renderResults = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#667eea]"></div>
        </div>
      );
    }

    if (results.length === 0 && query.trim()) {
      return (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600 mb-4">Try searching for something else</p>
          <div className="text-sm text-gray-500">
            <p className="mb-2">Popular searches:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['BOMG Overview', 'Product Master', 'Pricing Engine', 'Getting Started'].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Group results by type with priority order
    const typeOrder = ['category', 'tutorial', 'glossary', 'product'];
    const groupedResults = results.reduce((acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    }, {} as Record<string, typeof results>);

    // Sort types by priority order
    const sortedTypes = Object.keys(groupedResults).sort((a, b) => {
      const indexA = typeOrder.indexOf(a);
      const indexB = typeOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    // Get active tab results
    const activeResults = activeTab === 'all' 
      ? results 
      : (groupedResults[activeTab] || []);

    const typeColors = {
      category: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100', iconText: 'text-orange-600', text: 'text-orange-700', active: 'bg-orange-600 text-white' },
      tutorial: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100', iconText: 'text-green-600', text: 'text-green-700', active: 'bg-green-600 text-white' },
      glossary: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100', iconText: 'text-purple-600', text: 'text-purple-700', active: 'bg-purple-600 text-white' },
      product: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-100', iconText: 'text-blue-600', text: 'text-blue-700', active: 'bg-blue-600 text-white' }
    };

    return (
      <div className="space-y-4">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 -mb-px">
            {sortedTypes.map((type) => {
              const typeResults = groupedResults[type] as any[];
              const Icon = getResultIcon(type);
              const colors = typeColors[type as keyof typeof typeColors] || typeColors.tutorial;
              const isActive = activeTab === type;
              
              return (
                <button
                  key={type}
                  onClick={() => {
                    setActiveTab(type);
                    setSelectedIndex(0);
                  }}
                  className={cn(
                    "flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                    isActive
                      ? `${colors.active} border-current`
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-transparent"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span>{getResultTypeLabel(type)}</span>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-xs",
                    isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                  )}>
                    {typeResults.length}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Active Tab Content */}
        <div className="py-4">
          {activeResults.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {getResultTypeLabel(activeTab)} results</h3>
              <p className="text-gray-600">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeResults.map((result: any, index: number) => {
                const globalIndex = results.indexOf(result);
                const isSelected = selectedIndex === globalIndex;
                const Icon = getResultIcon(result.type);
                const colors = typeColors[result.type as keyof typeof typeColors] || typeColors.tutorial;
                
                return (
                  <motion.button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={cn(
                      "w-full flex items-start space-x-3 p-4 rounded-lg text-left transition-all border",
                      isSelected
                        ? "bg-white border-2 shadow-md"
                        : "bg-white hover:bg-gray-50 hover:shadow-sm border-gray-200"
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        colors.icon
                      )}>
                        <Icon className={cn("w-5 h-5", colors.iconText)} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 mb-1.5">{result.title || result.term}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {result.description || result.definition || result.content}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {result.category && (
                          <span className="inline-block px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md">
                            {result.category}
                          </span>
                        )}
                        {result.difficulty && (
                          <span className="inline-block px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-md">
                            {result.difficulty}
                          </span>
                        )}
                        {result.type === 'glossary' && (
                          <span className="inline-block px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-md">
                            Glossary
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className={cn("w-5 h-5 flex-shrink-0 mt-1 text-gray-400")} />
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render suggestions
  const renderSuggestions = () => {
    const recentSearches: string[] = [];
    const popularSearches = ['BOMG Overview', 'Product Master', 'Pricing Engine', 'Getting Started'];
    
    return (
      <div className="space-y-6">
        {recentSearches.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Recent Searches
            </h3>
            <div className="space-y-1">
              {recentSearches.slice(0, 5).map((term, index) => (
                <button
                  key={term}
                  onClick={() => handleSuggestionClick(term)}
                  className={cn(
                    "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                    selectedIndex === index
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50"
                  )}
                >
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{term}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Popular Searches
          </h3>
          <div className="space-y-1">
            {popularSearches.slice(0, 5).map((term, index) => {
              const globalIndex = recentSearches.length + index;
              return (
                <button
                  key={term}
                  onClick={() => handleSuggestionClick(term)}
                  className={cn(
                    "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                    selectedIndex === globalIndex
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50"
                  )}
                >
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{term}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        {/* Modal */}
        <motion.div
          className="relative bg-white h-full flex flex-col"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search tutorials, categories, glossary, and more..."
                  className="w-full pl-10 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                />
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Keyboard shortcuts hint */}
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">↑↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Enter</kbd>
                  <span>Select</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd>
                  <span>Close</span>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Command className="w-4 h-4" />
                <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">K</kbd>
                <span>to open</span>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              {showSuggestions ? renderSuggestions() : renderResults()}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SearchModal;
